/**
 * Image Validation Utility
 * 
 * A creator-friendly, forgiving image validation system that prioritizes
 * ease of use over technical perfection.
 * 
 * Key principles:
 * - Use soft warnings instead of hard rejections
 * - Accept images within reasonable aspect ratio ranges
 * - Auto-crop/auto-adjust by default
 * - Only block truly incompatible images
 */

// Aspect ratio ranges (not exact values)
export const IMAGE_SPECS = {
  // Hero banners: accept 2:1 to 3:1 (was 2.2-2.5)
  HERO_BANNER: {
    name: 'Hero Banner',
    minAspect: 2.0,      // Accept anything from 2:1
    maxAspect: 3.0,      // Up to 3:1
    targetAspect: 2.33,  // Ideal 21:9
    minWidth: 1200,      // Relaxed from 1920
    minHeight: 400,      // Relaxed from 820
    recWidth: 2560,
    recHeight: 1100,
    maxFileSize: 15 * 1024 * 1024, // 15MB
    // Absolute rejection threshold - only reject extreme portraits
    rejectIfAspectBelow: 1.3, // Reject very tall portraits
  },
  
  // Covers: accept 0.5:1 to 0.85:1 (portrait) (was strict 2:3 ±2%)
  COVER: {
    name: 'Cover',
    minAspect: 0.5,      // Accept 1:2 (very tall)
    maxAspect: 0.85,     // Up to ~5:6
    targetAspect: 0.667, // Ideal 2:3
    minWidth: 600,       // Relaxed from 1200
    minHeight: 800,      // Relaxed from 1800
    recWidth: 1600,
    recHeight: 2400,
    maxFileSize: 10 * 1024 * 1024, // 10MB
    // Absolute rejection threshold - only reject very wide images
    rejectIfAspectAbove: 1.2, // Reject landscape images for covers
  },
  
  // Thumbnails: accept 0.9:1 to 1.1:1 (roughly square)
  THUMBNAIL: {
    name: 'Thumbnail',
    minAspect: 0.8,      // Accept slightly tall
    maxAspect: 1.25,     // Accept slightly wide
    targetAspect: 1.0,   // Ideal square
    minWidth: 200,       // Relaxed minimum
    minHeight: 200,
    recWidth: 600,
    recHeight: 600,
    maxFileSize: 5 * 1024 * 1024, // 5MB
    // Never reject thumbnails based on aspect - always crop
    rejectIfAspectBelow: 0, 
    rejectIfAspectAbove: Infinity,
  },
  
  // Gallery/artwork: accept almost anything
  GALLERY: {
    name: 'Artwork',
    minAspect: 0.25,     // Accept 1:4 tall
    maxAspect: 4.0,      // Accept 4:1 wide
    targetAspect: null,  // No target - preserve original
    minWidth: 400,
    minHeight: 400,
    recWidth: 1920,
    recHeight: 1920,
    maxFileSize: 20 * 1024 * 1024, // 20MB
    // Never reject gallery images based on aspect
    rejectIfAspectBelow: 0,
    rejectIfAspectAbove: Infinity,
  },
};

/**
 * Validate an image and return friendly, actionable feedback
 * 
 * @param {File} file - The image file to validate
 * @param {string} type - One of: HERO_BANNER, COVER, THUMBNAIL, GALLERY
 * @returns {Promise<ValidationResult>}
 */
export const validateImage = (file, type) => {
  return new Promise((resolve) => {
    const spec = IMAGE_SPECS[type];
    if (!spec) {
      resolve({
        valid: false,
        error: 'Unknown image type',
        canUpload: false,
      });
      return;
    }

    const img = new Image();
    img.onload = () => {
      const { width, height } = img;
      const aspect = width / height;
      
      const result = {
        width,
        height,
        aspect,
        fileSize: file.size,
        
        // Is the aspect within the acceptable range?
        isInRange: aspect >= spec.minAspect && aspect <= spec.maxAspect,
        
        // Is it close to target (within 15%)?
        isCloseToTarget: spec.targetAspect 
          ? Math.abs(aspect - spec.targetAspect) / spec.targetAspect <= 0.15
          : true,
        
        // Should we absolutely reject this?
        shouldReject: (spec.rejectIfAspectBelow && aspect < spec.rejectIfAspectBelow) ||
                      (spec.rejectIfAspectAbove && aspect > spec.rejectIfAspectAbove),
        
        // Is resolution sufficient?
        meetsMinResolution: width >= spec.minWidth && height >= spec.minHeight,
        
        // File size ok?
        fileSizeOk: file.size <= spec.maxFileSize,
        
        // Overall
        canUpload: false,
        needsCrop: false,
        warning: null,
        error: null,
        friendlyMessage: null,
      };
      
      // Determine outcome
      if (!result.fileSizeOk) {
        result.canUpload = false;
        result.error = `Image too large (${(file.size / 1024 / 1024).toFixed(1)}MB). Maximum: ${(spec.maxFileSize / 1024 / 1024).toFixed(0)}MB.`;
        result.friendlyMessage = "This image is too large. Try compressing it or using a smaller version.";
      } else if (result.shouldReject) {
        result.canUpload = false;
        result.error = getIncompatibleMessage(type, aspect, width, height);
        result.friendlyMessage = result.error;
      } else if (!result.meetsMinResolution) {
        // Soft warning - still allow but warn
        result.canUpload = true;
        result.needsCrop = false;
        result.warning = `Image is smaller than recommended (${spec.recWidth}×${spec.recHeight}px). ` +
          `Your image: ${width}×${height}px. It may appear blurry.`;
        result.friendlyMessage = "This image is a bit small — it might look blurry, but we'll make it work!";
      } else if (!result.isInRange) {
        // Outside range but not rejected - auto-crop with warning
        result.canUpload = true;
        result.needsCrop = true;
        result.warning = getAdjustmentMessage(type, aspect);
        result.friendlyMessage = result.warning;
      } else if (!result.isCloseToTarget && spec.targetAspect) {
        // In range but not close to target - auto-crop available
        result.canUpload = true;
        result.needsCrop = true;
        result.warning = "We'll adjust this image automatically for best display.";
        result.friendlyMessage = result.warning;
      } else {
        // Perfect or close enough!
        result.canUpload = true;
        result.needsCrop = false;
        result.friendlyMessage = null;
      }
      
      resolve(result);
    };
    
    img.onerror = () => {
      resolve({
        valid: false,
        error: 'Could not read image. Please try a different file.',
        canUpload: false,
      });
    };
    
    img.src = URL.createObjectURL(file);
  });
};

/**
 * Get a friendly message for incompatible images
 */
const getIncompatibleMessage = (type, aspect, width, height) => {
  switch (type) {
    case 'HERO_BANNER':
      return `This image is too tall for a hero banner. We need a wide, landscape image (like a movie poster or panorama). ` +
        `Your image (${width}×${height}) is more portrait-shaped.`;
    case 'COVER':
      return `This image is too wide for a cover. We need a tall, portrait image (like a book cover). ` +
        `Your image (${width}×${height}) is more landscape-shaped.`;
    default:
      return `This image shape isn't compatible with ${IMAGE_SPECS[type]?.name || 'this format'}.`;
  }
};

/**
 * Get a friendly message for images that will be auto-adjusted
 */
const getAdjustmentMessage = (type, aspect) => {
  const spec = IMAGE_SPECS[type];
  if (!spec) return "We'll adjust this image for best display.";
  
  if (aspect < spec.minAspect) {
    return type === 'HERO_BANNER' 
      ? "This image is a bit tall — we'll crop it to fit the banner area."
      : "This image is a bit wide — we'll crop it to fit.";
  } else if (aspect > spec.maxAspect) {
    return type === 'COVER'
      ? "This image is a bit wide — we'll crop it to fit the cover area."
      : "This image is a bit tall — we'll crop it to fit.";
  }
  
  return "We'll adjust this image automatically for best display.";
};

/**
 * Check if an image needs cropping (returns quickly without full validation)
 */
export const checkNeedsCrop = (file, type) => {
  return new Promise((resolve) => {
    const spec = IMAGE_SPECS[type];
    if (!spec) {
      resolve({ needsCrop: false, canCrop: false });
      return;
    }

    const img = new Image();
    img.onload = () => {
      const aspect = img.width / img.height;
      const isInRange = aspect >= spec.minAspect && aspect <= spec.maxAspect;
      const isCloseToTarget = spec.targetAspect 
        ? Math.abs(aspect - spec.targetAspect) / spec.targetAspect <= 0.15
        : true;
      
      resolve({
        width: img.width,
        height: img.height,
        aspect,
        needsCrop: !isInRange || (!isCloseToTarget && spec.targetAspect),
        canCrop: img.width >= spec.minWidth / 2 && img.height >= spec.minHeight / 2,
      });
    };
    img.onerror = () => resolve({ needsCrop: false, canCrop: false });
    img.src = URL.createObjectURL(file);
  });
};

/**
 * Format file size for display
 */
export const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

/**
 * Get human-readable aspect ratio
 */
export const formatAspectRatio = (aspect) => {
  if (Math.abs(aspect - 1) < 0.1) return '1:1 (square)';
  if (Math.abs(aspect - 0.667) < 0.1) return '2:3 (portrait)';
  if (Math.abs(aspect - 1.5) < 0.1) return '3:2 (landscape)';
  if (Math.abs(aspect - 1.78) < 0.1) return '16:9 (widescreen)';
  if (Math.abs(aspect - 2.33) < 0.15) return '21:9 (ultrawide)';
  
  if (aspect < 1) {
    return `1:${(1/aspect).toFixed(1)} (portrait)`;
  }
  return `${aspect.toFixed(1)}:1 (landscape)`;
};

export default {
  IMAGE_SPECS,
  validateImage,
  checkNeedsCrop,
  formatFileSize,
  formatAspectRatio,
};
